"use client";
import {
  BankMandateModal,
  ButtonComponent,
  LoaderComponent,
  PaperComponent,
  SecureDataComponent,
  SnackbarComponent,
  TextFieldComponent,
} from "@/components";
import { useRouter } from "@/i18n/routing";
import {
  APP_KEYS,
  BANK_SCHEMA,
  GET_PRODUCT_APPLICATION,
  MANDATE_BANK_SETUP_NOTIFICATION,
  NUMBER_REGEX,
  PAYMENT_METHODS_MAPPING,
  PRODUCT_HANDLER,
  STAGE,
  STAGE_BASED_ROUTING,
  SUBSTAGE,
  WEB_COLORS,
  WEB_EVENTS,
  errorValidator,
} from "@/lib/constants";
import ENV from "@/lib/constants/EnvVariables";
import { useFooter } from "@/lib/context/FooterContext";
import useErrorHandling from "@/lib/hooks/errorhandling";
import useLogoutHook from "@/lib/hooks/logoutHook";
import useStageErrorHandler from "@/lib/hooks/stageErrorHandler";
import {
  useBankMandateMutation,
  useBankMandateStatusMutation,
  useBankPennyDropMutation,
  useGetApplicationQuery,
  useGetApplicationStageConfigQuery,
  useGetCustomerQuery,
  useLazyTriggerUcicQuery,
  store,
} from "@/lib/redux";
import { setModalAttributes } from "@/lib/redux/slice/ModalSlice";
import { Info } from "@mui/icons-material";
import {
  Button,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { useTranslations } from "next-intl";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ErrorModal from "../../repayment-mode/error-modal";
import IfscDrawer from "./ifsc-drawer";
import ROUTES from "@/lib/constants/PageRoutes";
import { checkForRpl } from "@/lib/utils/rpl.utils";
import { sendEventAnalytics } from "@/lib/hooks/eventAnalytics";
import { usePolling } from "@/lib/hooks/common.hooks";
import PennyDropPollingModal from "../PennyDropPollingModal";
import { sessionStorageUtil } from "@/lib/utils/common.utils";
import { useSearchParams } from "next/navigation";
import CurrentAccountWarningModal from "@/screens/(offer)/offers/CurrentAccountWarningModal";
import { setCurrentAccountWarningShown } from "@/lib/redux/slice/ApplicationSlice";

const POOLING_INTERVAL = 15000;
const POOLING_TIME = 60000;

const MANDATE_RETRY_COUNT_LIMIT = ENV.MANDATE_RETRY_COUNT_LIMIT;
const FORM_BLANK_VALUES = {
  bankName: "",
  accountNumber: "",
  confirmAccountNumber: "",
  bankIFSCCode: "",
  setSelectedBank: {
    ifscCode: "",
    bankName: "",
    state: "",
    city: "",
    address: "",
  },
  BankList: [],
};

const BankDetails = ({ product }) => {
  const t = useTranslations("RepaymentBanking");
  const router = useRouter();
  const [currentAccountModal, setCurrentAccountModal] = useState(false);
  const { setActions } = useFooter();
  const searchParams = useSearchParams();
  const redirectSource = searchParams.get("redirectSource");
  const paramMandateId = searchParams.get("mandateId");
  const paramMandateStatus = searchParams.get("mandate_status");
  const dispatch = useDispatch();
  const [showLoading, setShowLoading] = useState(false);
  const [openIfscModal, setOpenIfscModal] = useState(false);
  const [openBankModal, setOpenBankModal] = useState(false);
  const [formReset, setFormReset] = useState(false);
  const pennyDropPollingModalRef = useRef();
  const [errorModalDetails, setErrorModalDetails] = useState({
    label: "",
    subLabel: "",
    modalErrorType: "",
    buttonLabel: "Retry",
  });
  const { handleLogout } = useLogoutHook();
  const [modalVisible, setModalVisible] = useState(false);
  const [pollingModalData, setPollingModalData] = useState({
    open: false,
    btnDisabled: true,
  });

  const params = useSearchParams();
  const modeOfPayment = params.get("mode") || "debitCard";
  /***************************************RTK************************************ */
  //get customer details
  const { data: customerData, isLoading: fetchingCustomerData } =
    useGetCustomerQuery();
  // fetch application data from get api
  const {
    data: applicationData,
    error: errorGetApplication,
    isLoading: applicationLoading,
    refetch: refetchApplicationData,
    isSuccess: isSuccessApplication,
  } = useGetApplicationQuery("", {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  const [
    triggerUcic,
    { data: ucicData, isLoading: ucicLoading, error: ucicError },
  ] = useLazyTriggerUcicQuery();

  const productApplication = GET_PRODUCT_APPLICATION(
    PRODUCT_HANDLER[product],
    applicationData?.data,
  );

  const isRPL = checkForRpl(productApplication);

  const {
    data: appStageConfigData,
    isLoading: isLoadingAppStageConfig,
    error: appStageConfigError,
    refetch: refetchAppStageConfig,
  } = useGetApplicationStageConfigQuery(
    {
      appId: productApplication?.appId,
      stage: productApplication?.stage,
    },
    {
      skip: !productApplication?.appId || !productApplication?.stage,
      refetchOnMountOrArgChange: true,
    },
  );

  const appStageConfig = useMemo(() => {
    return {
      pollingSubStages: appStageConfigData?.data?.subStageHold || [],
      stage: appStageConfigData?.metaData?.stage,
    };
  }, [appStageConfigData]);

  const getCurrentAccountWarningShown = () =>
    store.getState()?.applicationSlice?.currentAccountWarningShown;

  const handlePollingStopCondition = useCallback(
    (data) => {
      const prodApp = GET_PRODUCT_APPLICATION(PRODUCT_HANDLER[product], data);
      const { stage, subStage } = prodApp;

      const ucic2flow = stage === STAGE.MANDATE;
      const ucic1flow = stage === STAGE.MANDATE_PENNYDROP;

      if (ucic1flow || ucic2flow) {
        return (
          !appStageConfig?.pollingSubStages.includes(subStage) &&
          stage === appStageConfig?.stage
        );
      }

      return true;
    },
    [product, appStageConfig],
  );

  const immediate = useMemo(() => {
    const ucic2Flow =
      productApplication?.stage === STAGE.MANDATE &&
      appStageConfig?.pollingSubStages?.includes(productApplication?.subStage);

    const ucic1flow =
      productApplication?.stage === STAGE.MANDATE_PENNYDROP &&
      appStageConfig?.pollingSubStages?.includes(productApplication?.subStage);

    let openPollModal = isRPL && (ucic2Flow || ucic1flow);

    return openPollModal;
  }, [
    isRPL,
    productApplication?.stage,
    productApplication?.subStage,
    appStageConfig,
  ]);

  const handlePollingOnComplete = useCallback(
    (res, err, { maxAttemptsReached, resetPolling: reset }) => {
      if (maxAttemptsReached) {
        // if polling ended due to reaching max attempts show retry button in modal
        setPollingModalData((i) => ({
          ...i,
          btnDisabled: false,
          pollingEnded: true,
          open: true,
        }));
      } else {
        // if polling ended due to stop condition met, close the modal and show success snackbar for non error cases
        if (!err) {
          setPollingModalData((i) => ({
            ...i,
            btnDisabled: true,
            pollingEnded: false,
            open: false,
          }));
        }
      }
      reset();
      pennyDropPollingModalRef?.current?.resetTimeLeft();
    },
    [],
  );

  const getApplicationPollingWithTriggerUcic = useCallback(async () => {
    const appData = await refetchApplicationData()?.unwrap();
    const prodApp = GET_PRODUCT_APPLICATION(
      PRODUCT_HANDLER[product],
      appData?.data,
    );
    if (prodApp?.subStage === SUBSTAGE.UCIC_REQUEST_FAILED) {
      const triggerData = await triggerUcic({
        appId: prodApp?.appId,
        stage: prodApp?.stage,
      });
      const { isError, isSuccess } = triggerData;
    }
    return appData;
  }, [refetchApplicationData, triggerUcic]);

  const {
    data: applicationDataPolling,
    isPolling,
    isFetching,
    attempt,
    resetPolling,
    startPolling,
  } = usePolling({
    pollFn: getApplicationPollingWithTriggerUcic,
    interval: POOLING_INTERVAL,
    totalPollingTime: POOLING_TIME,
    onStart: () => {
      setPollingModalData({
        btnDisabled: true,
        pollingEnded: false,
        open: true,
      });
    },
    immediate: immediate, // start immediate polling if RPL and at penny drop stage
    stopCondition: handlePollingStopCondition,
    onComplete: handlePollingOnComplete,
  });

  const retryPollingBtnClick = useCallback(() => {
    resetPolling();
    pennyDropPollingModalRef?.current?.resetTimeLeft();
    startPolling();
    setPollingModalData({
      btnDisabled: true,
      pollingEnded: false,
      open: true,
    });
  }, [resetPolling, startPolling]);

  const handleApplicationPollingStopCondition = useCallback(
    (response) => {
      const application = response?.data?.applications?.find(
        (app) => app.product_type === PRODUCT_HANDLER[product],
      );
      const subStage = application?.subStage;
      const shouldStop =
        subStage != null && subStage !== SUBSTAGE.MANDATE_POLLING;

      return shouldStop;
    },
    [product],
  );
  const {
    data: polledApplicationData,
    isPolling: isPollingApplication,
    isFetching: isFetchingApplication,
    resetPolling: resetPollingApplication,
    startPolling: startPollingApplication,
  } = usePolling({
    pollFn: async () => {
      const result = await refetchApplicationData();
      return result;
    },
    interval: 3000,
    stopCondition: handleApplicationPollingStopCondition,
    onComplete: async (data) => {
      if (paramMandateStatus?.toLowerCase() === "failure") {
        setErrorModalDetails({
          modalErrorType: "emandateFailed",
          onBtnClick: handleOnMandateFailed,
        });
        handleMandateRetryCheck(mandateStatusData?.data?.data);
        setModalVisible(true);
      }
    },
    onStart: () => {},
  });

  const m = useTranslations("ErrorModal.mandateLimitExhausted");
  //create mandate API call from Bank Details screen
  const [
    createMandate,
    {
      data: createMandateData,
      error: errorCreateMandate,
      isLoading: createMandateLoading,
      isSuccess: isSuccessCreateMandate,
    },
  ] = useBankMandateMutation();

  //post e-mandate status check
  const [
    mandateStatus,
    {
      data: mandateStatusData,
      error: errorMandateStatus,
      isError: isErrorMandate,
      isSuccess: isSuccessMandate,
      isLoading: mandateStatusLoading,
    },
  ] = useBankMandateStatusMutation();

  const [
    callPennyDrop,
    { data: pennyDropData, error: errorPennyDrop, isLoading: pennyDropLoading },
  ] = useBankPennyDropMutation();

  const isAAorPerfiosFlow = ["AA", "PERFIOS"]?.includes(
    productApplication?.bankVerificationMode,
  );

  const mandateExhaustedObj = useMemo(() => {
    const eCount = productApplication?.eMandateRetryCount;
    const upiCount = productApplication?.upiMandateRetryCount;
    const stage = productApplication?.stage;
    const subStage = productApplication?.subStage;

    const allExhausted =
      stage === STAGE.MANDATE &&
      ((eCount === ENV.MAX_E_MANDATE_RETRY_COUNT &&
        upiCount === ENV.MAX_UPI_MANDATE_RETRY_COUNT) ||
        subStage === SUBSTAGE.MANDATE_LIMIT_EXHAUSTED);
    return {
      open: allExhausted,
      routeToHome: allExhausted,
    };
  }, [productApplication]);

  const handleMandateRetryCheck = (data) => {
    //later remove this for create mandate api is live
    return;
    !modalVisible && setModalVisible(true);
    const hasExhausted = MANDATE_RETRY_COUNT_LIMIT <= data?.mandateRetryCount;
    setErrorModalDetails((d) => {
      let prev = { ...d };
      //if label isnot there
      if (d.subLabel) {
        prev.error = data?.mandateRetryCountMessage;
      }
      if (hasExhausted) {
        if (!d.label) {
          prev.label = "Mandate limit Exhausted";
        }
        prev.onBtnClick = function () {
          setModalVisible(false);
          router.push("/");
        };
      }
      return prev;
    });
  };
  const isError =
    errorGetApplication ||
    errorMandateStatus ||
    errorCreateMandate ||
    appStageConfigError ||
    errorPennyDrop;
  const isloading =
    applicationLoading ||
    createMandateLoading ||
    mandateStatusLoading ||
    pennyDropLoading ||
    isLoadingAppStageConfig ||
    isPollingApplication;

  const createRplPennyDrop = async ({
    inputBankName,
    inputBankIFSCCode,
    inputAccountNumber,
    requestedData,
  }) => {
    const { bankName, bankIFSCCode, bankAccountNumber } = productApplication;

    const bankDetailsChanged = !(
      bankName?.trim() === inputBankName?.trim() &&
      bankIFSCCode?.trim() === inputBankIFSCCode?.trim() &&
      bankAccountNumber?.trim() === inputAccountNumber?.trim()
    );

    if (bankDetailsChanged) {
      sendEventAnalytics({
        ...productApplication,
        ...customerData?.data,
        eventName: WEB_EVENTS.RL_BANK_ACCOUNT_CHANGED,
      });
    } else {
      sendEventAnalytics({
        ...productApplication,
        ...customerData?.data,
        eventName: WEB_EVENTS.RL_ON_BANK_ACCOUNT_CONFIRMATION,
      });
    }
    // PENNYDROP call for RPL;
    const response = await callPennyDrop(requestedData);

    if (response?.error) {
      const error = response?.error;

      // Handle retry exhausted (400 error)
      if (400 <= error?.status) {
        dispatch(
          setModalAttributes({
            open: true,
            message: {
              label: "Verification Failed",
              subLabel:
                error?.data?.message ||
                error?.data?.detail?.message ||
                "Your Bank Validation attempt has been exhausted, please contact our customer support",
            },
            type: "ATTEMPT_ERROR",
          }),
        );
        return;
      }

      // Handle other errors
      SnackbarComponent({
        error: true,
        errorMessage:
          error?.data?.message ||
          (error?.data?.detail?.message ?? error?.data?.detail) ||
          "Something went wrong",
      });
      return;
    }

    const pennyDropRes = response?.data;

    // Handle success case
    if (pennyDropRes?.data?.isVerified) {
      SnackbarComponent({
        success: true,
        successMessage: "Bank details verified successfully",
      });
      refetchApplicationData();
      retryPollingBtnClick();
      // resetPolling();
      // startPolling();
      return;
    }

    // Handle failure/retry case (name mismatch or verification failed)
    if (!pennyDropRes?.data?.isVerified) {
      const {
        reason,
        pennyDropRetryCountMessage,
        pennyDropRetryCount,
        maximumPennyDropRetryCount,
        message,
      } = pennyDropRes?.data;

      let modalObject = {
        open: true,
        message: {
          label: reason || "Verification Failed",
          subLabel: message,
          error: pennyDropRetryCountMessage,
        },
        routeHome: maximumPennyDropRetryCount <= pennyDropRetryCount,
        type: "ATTEMPT_ERROR",
      };
      if (reason?.toLowerCase() === "name mismatch") {
        modalObject = {
          open: true,
          message: `The bank account must belong to the person applying for the loan ${customerData?.data?.firstName} ${customerData?.data?.lastName}. In your case, it appears the account is registered under a different name. Please try again with your own bank details.`,
          type: SUBSTAGE?.KYC_BANK_NAME_MATCH_CREDIT_QUEUE,
          error: pennyDropRetryCountMessage,
        };
      }

      dispatch(setModalAttributes(modalObject));
      return;
    }
  };

  const handleCurrentAccountChecks = (
    inputBankName,
    inputBankIFSCCode,
    inputAccountNumber,
  ) => {
    const { bankName, bankIFSCCode, bankAccountNumber } = productApplication;

    const bankDetailsChanged = !(
      bankName?.trim() === inputBankName?.trim() &&
      bankIFSCCode?.trim() === inputBankIFSCCode?.trim() &&
      bankAccountNumber?.trim() === inputAccountNumber?.trim()
    );

    if (bankDetailsChanged) {
      return true;
    }
    SnackbarComponent({
      error: true,
      errorMessage: "Please provide different bank account details to proceed.",
    });
    return false;
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      bankName: productApplication?.bankName || "",
      accountNumber: productApplication?.bankAccountNumber || "",
      confirmAccountNumber: productApplication?.bankAccountNumber || "",
      bankIFSCCode: productApplication?.bankIFSCCode || "",
      setSelectedBank: {
        ifscCode: "",
        bankName: "",
        state: "",
        city: "",
        address: "",
      },
      BankList: [],
    },
    validationSchema: BANK_SCHEMA,
    onSubmit: async (values) => {
      const inputBankName = values?.bankName?.trim();
      const inputAccountNumber = values?.accountNumber?.trim();
      const inputBankIFSCCode = values?.bankIFSCCode?.trim()?.toUpperCase();

      if (
        productApplication?.subStage === SUBSTAGE.PENNYDROP_RETRY &&
        getCurrentAccountWarningShown()
      ) {
        const continueAction = handleCurrentAccountChecks(
          inputBankName,
          inputBankIFSCCode,
          inputAccountNumber,
        );
        if (!continueAction) return;
      }

      let requestedData = {
        appId: productApplication?.appId,
        bankName: inputBankName,
        accountNumber: inputAccountNumber,
        ifsc: inputBankIFSCCode,
      };
      if (
        isRPL &&
        !appStageConfig?.pollingSubStages.includes(productApplication?.subStage)
      ) {
        createRplPennyDrop({
          inputBankName,
          inputBankIFSCCode,
          inputAccountNumber,
          requestedData,
        });
        return;
      } else if (!isRPL) {
        createMandate({
          ...requestedData,
          paymentMethodType: PAYMENT_METHODS_MAPPING[modeOfPayment],
        });
        return;
      }

      dispatch(setCurrentAccountWarningShown(false));
    },
  });

  const handleCurrentAccountWarningSuccess = useCallback(() => {
    setCurrentAccountModal(false);
    dispatch(setCurrentAccountWarningShown(true));
  }, [dispatch, setCurrentAccountModal]);

  const handleOnMandateFailed = () => {
    setModalVisible(false);
    if (mandateStatusData?.data?.subStage === SUBSTAGE?.MANADATE_FAILED) {
      sessionStorageUtil.remove("mandateId");
    }
  };

  /***************************************EFFECT************************************ */
  useErrorHandling(isError);

  useEffect(() => {
    if (formik.values.bankName !== productApplication?.bankName) {
      formik?.setFieldValue("accountNumber", "");
      formik?.setFieldValue("confirmAccountNumber", "");
      formik?.setFieldValue("bankIFSCCode", "");
    }
  }, [formik.values.bankName]);

  useEffect(() => {
    if (mandateExhaustedObj?.open) {
      setErrorModalDetails({
        label: m("label"),
        subLabel: m("subLabel"),
        buttonLabel: "Ok",
        onBtnClick: function () {
          setModalVisible(false);
          mandateExhaustedObj?.routeToHome && router.push("/");
        },
        hideCloseBtn: true,
      });
      setModalVisible(true);
    }

    const stage = productApplication?.stage;
    const subStage = productApplication?.subStage;
    const ucicDecision = productApplication?.ucicDecision?.toLowerCase();
    const previousStage = productApplication?.previousStage;

    if (
      stage === STAGE.MANDATE_PENNYDROP &&
      subStage === SUBSTAGE.PENNYDROP_RETRY
    ) {
      setCurrentAccountModal(true);
    } else if (
      stage === STAGE.MANDATE &&
      subStage === SUBSTAGE.PENNYDROP_RETRY &&
      !getCurrentAccountWarningShown()
    ) {
      setCurrentAccountModal(true);
    }

    if (isRPL && stage) {
      if (stage === STAGE.MANDATE && appStageConfig?.stage !== STAGE.MANDATE) {
        refetchAppStageConfig();
        return;
      }
      // Polling for UCIC 2 in RPL
      if (
        (previousStage === STAGE.MANDATE_PENNYDROP ||
          previousStage === STAGE.CREDIT_QUEUE) &&
        stage === STAGE.MANDATE &&
        appStageConfig?.pollingSubStages.includes(subStage)
      ) {
        // keep user on this page and  polling continues until ucic is accepted or rejected
        if (!pennyDropPollingModalRef?.current?.getState()?.open) {
          // if pooling not happening, then start pooling
          retryPollingBtnClick();
        }
        return;
      }
      // UCIC 2 done, move to mandate for mandate creation
      const notPostMandateLanding = redirectSource !== "mandate"; // not from mandate flow landing
      // checking  !sessionStorageUtil?.has(APP_KEYS.MANDATE_ID) => not post mandate registration landing
      if (
        notPostMandateLanding &&
        // !sessionStorageUtil?.has(APP_KEYS.MANDATE_ID) &&
        stage === STAGE.MANDATE &&
        stage === appStageConfig?.stage &&
        appStageConfig?.pollingSubStages.length > 0 &&
        !appStageConfig?.pollingSubStages.includes(subStage)
      ) {
        router.push(STAGE_BASED_ROUTING(stage));
        return;
      }
    }

    // move to non-mandate stage in other cases
    if (stage && ![STAGE.MANDATE_PENNYDROP, STAGE.MANDATE].includes(stage)) {
      if (
        stage === STAGE.E_SIGN &&
        sessionStorageUtil.has(APP_KEYS.MANDATE_ID)
      ) {
        router.push(ROUTES(product).E_SIGN_INSTRUCTIONS);
        return;
      }
      router.push(STAGE_BASED_ROUTING(stage)); //naviagte to Esign screen
    }
    if (
      previousStage === STAGE?.CREDIT_QUEUE &&
      productApplication?.subStage !== SUBSTAGE.PENNYDROP_RETRY &&
      !sessionStorage.mandateId
    ) {
      enqueueSnackbar(
        "Verification Successful!. You can now continue with your loan application.",
        { variant: "success", autoHideDuration: 3000, preventDuplicate: true },
      );
    }
  }, [
    productApplication?.stage,
    isRPL,
    productApplication?.previousStage,
    productApplication?.ucicDecision,
    mandateExhaustedObj,
    productApplication?.subStage,
    retryPollingBtnClick,
    appStageConfig,
    redirectSource,
  ]);

  useEffect(() => {
    if (
      mandateStatusData &&
      mandateStatusData?.data &&
      mandateStatusData?.data?.stage
    ) {
      setShowLoading(false);
      if (
        mandateStatusData?.data?.stage !== null &&
        mandateStatusData?.data?.stage === STAGE?.MANDATE
      ) {
        setErrorModalDetails({
          modalErrorType: "mandateFailed",
          onBtnClick: handleOnMandateFailed,
        });
        handleMandateRetryCheck(mandateStatusData?.data?.data);
        setModalVisible(true);
      } else if (
        mandateStatusData?.data?.stage !== null &&
        mandateStatusData?.data?.stage === STAGE?.CREDIT_QUEUE
      ) {
        setErrorModalDetails({
          modalErrorType: "mandateFailedWithCreditQueue",
        });
        handleMandateRetryCheck(mandateStatusData?.data?.data);
        setModalVisible(true);
      } else if (
        mandateStatusData?.data?.stage !== null &&
        mandateStatusData?.data?.stage === STAGE?.E_SIGN
      ) {
        SnackbarComponent({
          success: true,
          successMessage: "E-mandate has been completed.",
        });
        refetchApplicationData(); //navigate to esign page
      } else if (
        mandateStatusData?.data?.stage !== null &&
        mandateStatusData?.data?.stage === STAGE?.VKYC
      ) {
        router.push(STAGE_BASED_ROUTING(STAGE.VKYC)); // navigate to the appropriate route
      }
    }
  }, [mandateStatusData, errorMandateStatus]);

  useEffect(() => {
    if (errorCreateMandate) {
      if (errorCreateMandate?.status === 401) {
        SnackbarComponent({
          error: isError,
          errorMessage: "Your session has expired. Please login again",
        });
        handleLogout(); //navigate to login screen
        return;
      } else if (
        errorCreateMandate?.data?.statusCode === 400 &&
        (errorCreateMandate?.data?.data?.reason?.toLowerCase() ===
          "name mismatch" ||
          errorCreateMandate?.data?.data?.message?.toLowerCase() ===
            "name mismatch" ||
          errorCreateMandate?.data?.data?.status?.toLowerCase() ===
            "name mismatch")
      ) {
        dispatch(
          setModalAttributes({
            open: true,
            message: `The bank account must belong to the person applying for the loan ${customerData?.data?.firstName} ${customerData?.data?.lastName}. In your case, it appears the account is registered under a different name. Please try again with your own bank details.`,
            type: SUBSTAGE?.KYC_BANK_NAME_MATCH_CREDIT_QUEUE,
          }),
        );
        return;
      } else if (errorCreateMandate?.status === 400) {
        if (errorCreateMandate?.data?.message) {
          dispatch(
            setModalAttributes({
              open: true,
              message: {
                subLabel: errorCreateMandate?.data?.message,
              },
              type: "INVALID_ACCOUNT_DETAILS",
            }),
          );
        } else if (errorCreateMandate?.data?.detail) {
          dispatch(
            setModalAttributes({
              open: true,
              message: {
                label: m("label"),
                subLabel:
                  errorCreateMandate?.data?.detail || "Please try again.",
              },
              type: "ATTEMPT_ERROR",
            }),
          );
        }
        return;
      } else if (errorCreateMandate?.status === 500) {
        dispatch(
          setModalAttributes({
            open: true,
            message:
              isError?.data?.message ||
              `We’re working on resolving the issue and will be back shortly. You
            can check back soon or explore other sections in the app meanwhile.`,
            type: "FAILED",
            // image: IMAGES.fail,
          }),
        );
      }
      if (errorCreateMandate?.status === 504) {
        dispatch(
          setModalAttributes({
            open: true,
            message: {
              label: "Gateway Timeout",
              subLabel:
                "The server took too long to respond. Please check your internet connection and try again.",
            },
            type: "GATEWAY_TIMEOUT",
          }),
        );
        return;
      }
      SnackbarComponent({
        error: true,
        errorMessage: errorValidator(isError),
      });
      return;
    }
    if (
      createMandateData?.data?.reason ===
      SUBSTAGE.UCIC_HUNTER_DECISION_POSTPONED_REJECT
    ) {
      refetchApplicationData();
      return;
    }
    if (createMandateData?.data?.ResCode === "EmRes001") {
      setShowLoading(true);
      // navigate to payment gateway LinkOneMoney
      sessionStorageUtil.set("mandateId", createMandateData?.data?.MdtID);
      sessionStorage.setItem(APP_KEYS.PRODUCT_TYPE, product);
      window.location.href = createMandateData?.data?.URL;
    }
    if (createMandateData?.data?.paymentLink) {
      setShowLoading(true);
      sessionStorage.setItem("mandateId", createMandateData?.data?.orderId);
      sessionStorage.setItem(APP_KEYS.PRODUCT_TYPE, product);
      window.location.href = createMandateData?.data?.paymentLink;
    }
  }, [productApplication?.stage, createMandateData, errorCreateMandate]);

  useEffect(() => {
    if (window !== undefined) {
      if (
        redirectSource === "mandate" &&
        paramMandateId &&
        sessionStorage.getItem("mandateId") &&
        productApplication?.appId
      ) {
        const requestData = {
          appId: productApplication?.appId,
          mandateId: sessionStorage.getItem("mandateId"),
        };
        mandateStatus(requestData)
          .unwrap()
          .catch((err) => {
            handleMandateRetryCheck(err?.data?.data);
          });
      }
    }
  }, [paramMandateId, redirectSource, productApplication?.appId]);

  useStageErrorHandler({
    stage: productApplication?.stage,
    prevStage: productApplication?.previousStage,
    message: productApplication?.loanReapplyMessage,
  });

  /***************************************ACTION************************************ */
  const handleFieldChange = (e, regex) => {
    if (regex(e.target.value)) {
      formik.handleChange(e);
    }
  };

  const onClickChangeBankBtn = () => {
    setFormReset(true);
    formik.resetForm();
    formik.setValues(FORM_BLANK_VALUES);
  };

  const handleSetSelectedBank = (value) => {
    if (value?.bankName !== "") {
      formik.setFieldValue("setSelectedBank", value);
      formik.setFieldValue("bankName", value?.bankName);
      formik.setFieldValue("bankIFSCCode", value?.bankIFSCCode);
    }
  };

  const onPressErrorButton = (errorType) => {
    setModalVisible(false);
    if (errorType === "mandateFailedWithCreditQueue") {
      router.push("/");
    }
  };

  useEffect(() => {
    setActions({
      onConfirm:
        productApplication?.subStage === SUBSTAGE?.MANDATE_POLLING
          ? null
          : formik.handleSubmit,
      disabledConfirmBtn:
        productApplication?.subStage === SUBSTAGE?.MANDATE_POLLING,
    });
  }, [setActions, productApplication?.subStage]);
  const disabledFields = useMemo(() => {
    if (productApplication?.subStage === SUBSTAGE.PENNYDROP_RETRY) {
      return false;
    }
    if (isRPL) {
      return (
        (productApplication?.stage === STAGE.MANDATE_PENNYDROP && !formReset) ||
        productApplication?.stage === STAGE.MANDATE
      );
    } else {
      return (
        productApplication?.stage === STAGE.MANDATE_PENNYDROP ||
        productApplication?.subStage ===
          SUBSTAGE.KYC_BANK_NAME_MATCH_CREDIT_QUEUE_APPROVED ||
        productApplication?.subStage === SUBSTAGE.E_MANDATE_CREATED_SUCCESS ||
        productApplication?.subStage === SUBSTAGE.E_MANDATE_ATTEMPT_EXHAUSTED
      );
    }
  }, [
    formReset,
    productApplication?.stage,
    productApplication?.subStage,
    isRPL,
  ]);

  const ifscDisabled = useMemo(() => {
    const isAtPennyDropStage =
      productApplication?.stage === STAGE.MANDATE_PENNYDROP ||
      productApplication?.subStage === SUBSTAGE.E_MANDATE_CREATED_SUCCESS ||
      productApplication?.subStage === SUBSTAGE.E_MANDATE_ATTEMPT_EXHAUSTED;
    const isRplMandate = isRPL && productApplication?.stage === STAGE.MANDATE;
    const hasValidAccountDetails =
      formik?.values?.bankName !== "" &&
      formik?.values?.accountNumber === formik.values.confirmAccountNumber &&
      formik?.values?.accountNumber?.length > 0;

    if (productApplication?.subStage === SUBSTAGE.PENNYDROP_RETRY) {
      return false;
    };
    if (isRPL) {
      return formReset
        ? !hasValidAccountDetails
        : isAtPennyDropStage || isRplMandate;
    } else {
      return isAtPennyDropStage || !hasValidAccountDetails;
    }
  }, [
    productApplication?.stage,
    productApplication?.subStage,
    isRPL,
    formik?.values?.bankName,
    formik?.values?.accountNumber,
    formik?.values?.confirmAccountNumber,
    formReset,
  ]);

  const showChangeBankBtn = useMemo(() => {
    if (productApplication?.subStage === SUBSTAGE.PENNYDROP_RETRY) {
      return false;
    }
    return (
      isRPL &&
      !formReset &&
      productApplication?.stage === STAGE.MANDATE_PENNYDROP
    );
  }, [formReset, productApplication?.stage, productApplication?.subStage, isRPL]);

  /***************************************RENDER************************************ */
  return (
    <>
      <LoaderComponent open={isloading || showLoading} />
      <PaperComponent
        paperStyle={{ backgroundColor: "rgba(242, 247, 247, 0.6)" }}
      >
        <Stack rowGap={2}>
          <Typography variant="h6" color={WEB_COLORS.primary}>
            {t("tittle")}
          </Typography>
          <Typography sx={{ textTransform: "capitalize" }}>
            {t("customername")}{" "}
            <strong
              style={{
                color: WEB_COLORS.primary,
              }}
            >{`${customerData?.data?.firstName?.toLowerCase() || ""} ${
              customerData?.data?.lastName?.toLowerCase() || ""
            }`}</strong>
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <TextFieldComponent
                  label={t("selectbank")}
                  cursor="none"
                  value={formik.values.bankName}
                  disabled={isAAorPerfiosFlow || disabledFields}
                  onClick={() =>
                    !isAAorPerfiosFlow &&
                    (isRPL ||
                      productApplication?.stage !== STAGE.MANDATE_PENNYDROP) &&
                    setOpenBankModal(true)
                  }
                  readOnly
                  onKeyDown={(event) => {
                    event.preventDefault();
                  }}
                  error={
                    formik.touched.bankName && Boolean(formik.errors.bankName)
                  }
                  errorMessage={formik.errors.bankName}
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <TextFieldComponent
                  name="accountNumber"
                  onChange={(e) => handleFieldChange(e, NUMBER_REGEX)}
                  onBlur={formik.handleBlur}
                  value={formik.values.accountNumber}
                  label={t("enteraccount")}
                  disabled={isAAorPerfiosFlow || disabledFields}
                  InputProps={{
                    readOnly: disabledFields,
                  }}
                  maxLength={20}
                  minLength={6}
                  error={
                    formik.touched.accountNumber &&
                    Boolean(formik.errors.accountNumber)
                  }
                  errorMessage={
                    formik.touched.accountNumber && formik.errors.accountNumber
                  }
                />
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <TextField
                  type="password"
                  name="confirmAccountNumber"
                  onChange={(e) => handleFieldChange(e, NUMBER_REGEX)}
                  onBlur={formik.handleBlur}
                  value={formik.values.confirmAccountNumber}
                  label={t("confirmaccount")}
                  disabled={isAAorPerfiosFlow || disabledFields}
                  InputProps={{
                    readOnly: disabledFields,
                  }}
                  inputProps={{ maxLength: 20 }}
                  minLength={6}
                  onPaste={(e) => e.preventDefault()}
                  sx={{ backgroundColor: "#fff" }}
                  error={
                    formik.touched.confirmAccountNumber &&
                    Boolean(formik.errors.confirmAccountNumber)
                  }
                />
                <FormHelperText sx={{ color: "#d32f2f" }}>
                  {formik.touched.confirmAccountNumber &&
                    formik.errors.confirmAccountNumber}
                </FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <TextFieldComponent
                  disabled={ifscDisabled}
                  inputPropsStyle={{ textTransform: "uppercase" }}
                  name="bankIFSCCode"
                  multiline={false}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.bankIFSCCode}
                  label={t("ifsccode")}
                  error={
                    formik.touched.bankIFSCCode &&
                    Boolean(formik.errors.bankIFSCCode)
                  }
                  errorMessage={
                    formik.touched.bankIFSCCode && formik.errors.bankIFSCCode
                  }
                  endAdornment={
                    <Button
                      disabled={ifscDisabled}
                      onClick={() => {
                        formik.values.bankName !== "" &&
                        formik.values.accountNumber ===
                          formik.values.confirmAccountNumber &&
                        formik.values.accountNumber.length > 0 &&
                        formik.values.confirmAccountNumber.length > 0
                          ? setOpenIfscModal(true)
                          : SnackbarComponent({
                              error: true,
                              errorMessage: t("ifscerrormessage"),
                            });
                      }}
                      sx={{
                        textTransform: "none",
                        width: "-webkit-fill-available",
                        maxWidth: 140,
                      }}
                    >
                      {t("ifscbutton")}
                    </Button>
                  }
                />
              </FormControl>
            </Grid>
          </Grid>
          {showChangeBankBtn && (
            <ButtonComponent
              variant="outlined"
              type="reset"
              buttonStyle={{
                width: "264px",
              }}
              width={"150px"}
              onClick={onClickChangeBankBtn}
            >
              Change Bank
            </ButtonComponent>
          )}

          <Stack>
            <Stack
              direction={"row"}
              alignItems={"center"}
              mt={1}
              marginLeft={-1.5}
            >
              <IconButton
                sx={{
                  color: "#FFC700",
                }}
              >
                <Info />
              </IconButton>
              <Typography color={WEB_COLORS.textFadeColor}>
                {t("note")}
              </Typography>
            </Stack>
            <Stack marginLeft={3.5}>
              {MANDATE_BANK_SETUP_NOTIFICATION()?.map(({ sNo, text }) => (
                <Typography key={sNo} color={WEB_COLORS.textFadeColor}>
                  {sNo} {text}
                </Typography>
              ))}
            </Stack>
          </Stack>
          <SecureDataComponent />
        </Stack>
      </PaperComponent>
      <IfscDrawer
        open={openIfscModal}
        handleDrawer={setOpenIfscModal}
        handleOnSubmit={handleSetSelectedBank}
        schema={{
          bankName: {
            disabled:
              isAAorPerfiosFlow ||
              productApplication?.stage === STAGE.MANDATE_PENNYDROP,
          },
        }}
        initialValues={{
          bankIFSCCode: "",
          bankName: formik.values.bankName,
          state: "",
          city: "",
          address: "",
        }}
      />
      <PennyDropPollingModal
        ref={pennyDropPollingModalRef}
        data={{
          totalPollingTime: POOLING_TIME,
          interval: POOLING_INTERVAL,
        }}
        open={pollingModalData.open}
        btnOnClick={retryPollingBtnClick}
        btnDisabled={pollingModalData.btnDisabled}
        pollingStart={isPolling || isFetching}
      />
      <BankMandateModal
        bankName={productApplication?.bankName}
        open={openBankModal}
        handleModal={setOpenBankModal}
        formik={formik}
        handleSelectedBank={handleSetSelectedBank}
        selectedBankDetails={formik.values.setSelectedBank}
      />
      <ErrorModal
        open={modalVisible}
        handleModal={setModalVisible}
        data={errorModalDetails}
        onPress={() => {
          onPressErrorButton(errorModalDetails?.modalErrorType);
        }}
      />
      <CurrentAccountWarningModal
        open={currentAccountModal}
        name={productApplication?.aadharFullName}
        onSuccess={handleCurrentAccountWarningSuccess}
      />
    </>
  );
};

export default BankDetails;
